/**
 * Request builder v2 (BENCH-V2-SPEC §6.1, §8): one chunk, many marked units, a JSON answer.
 *
 * - `system` (§6.1 order, v3 ED-88): the seed preamble verbatim, the route block, the always-on rules (C-DIR-V2-21, INV-DIR-22,
 *   C-DIR-V2-23, -DASH, -DIALOGUE), the output contract and, only when the run supplies them, the `DOCUMENT NOTES` block. It
 *   no longer carries the chunk's directives, so it varies only by route class (sentence / paragraph / mixed), the marker pair
 *   and the notes: one cache write per document and route class (§9.1).
 * - `user`: `CHUNK ¶a–¶b (version v)`, the retry preface (retry rounds), `Directives for this stretch (all mandatory):` (the
 *   directives of the chunk's targeted channels, with the run's refrains and frames, plus the v2 directives SR-01 and PT-02;
 *   repair mode: REPAIR_DIRECTIVES; never for a control-only request), the chunk text with every unit wrapped in
 *   `⟦U7⟧ … ⟦/U7⟧`, and a NOTES block with one entry per unit (channels, a one-line pattern, the quoted evidence spans, the
 *   route instruction). A retry round adds the previous version of each failing unit and the feedback (§6.7).
 *
 * The builder is independent of the provider: it returns the texts, the schema and the unit ids; WP6 maps them onto the
 * request (effort, `output_config`, caching, `max_tokens`). Texts are data (`lang/en/v3/directives.bench-v2.json`).
 */
import type { ChannelId } from '../channels/types.ts';
import dataJson from '../lang/en/v3/directives.bench-v2.json' with { type: 'json' };
import versionsJson from '../lang/en/v3/versions.bench-v3.json' with { type: 'json' };
import { type DirectiveEvidence, buildDirectives, directiveData } from './directives.ts';
import {
	GUARDRAILS_CONTRACT,
	GUARDRAILS_DIRECTIVES_HEADING,
	GUARDRAILS_RETRY_PREFACE,
	GUARDRAILS_REVIEW_CONTRACT,
	GUARDRAILS_REVIEW_MEANING_CONTRACT,
	GUARDRAILS_REVIEW_MEANING_VERSIONS_CONTRACT,
	GUARDRAILS_REVIEW_SYSTEM,
	GUARDRAILS_REVIEW_VERSIONS_CONTRACT,
	GUARDRAILS_STRATEGIES,
	GUARDRAILS_SYSTEM,
	GUARDRAILS_VERSIONS_CONTRACT,
	GUARDRAILS_VERSIONS_RETRY_PREFACE,
	type UnitRecord,
	WHY_MAX_CHARS,
	noteInstruction,
	recordLine,
	withGuardrails,
} from './guardrails.ts';
import { PREAMBLE, repairDirectives } from './prompts.ts';
import { heaviestReplaceable } from './tr-guard.ts';

interface V2Data {
	markers: { open: string; close: string };
	routeBlocks: { S: string; Pextra: string; judge: string };
	always: Array<{ id: string; text: string }>;
	outputContract: string;
	neutral: string;
	directives: Array<{ id: string; when: string[]; text: string; suffix?: string }>;
}
const DATA = dataJson as unknown as V2Data;

/** The alternative-versions data (BENCH-V3-PLAN §6.1; `lang/en/v3/versions.bench-v3.json`). */
interface VersionsData {
	order: { first: StrategyId[]; retry: StrategyId[] };
	strategies: Record<StrategyId, string>;
	contract: string;
	retryPreface: string;
	heading: string;
	line: string;
}
const VDATA = versionsJson as unknown as VersionsData;

/** The named strategies of the versions of one passage (BENCH-V3-PLAN §6.1): `edit` replaces `minimal` on a retry. */
export type StrategyId = 'minimal' | 'restructure' | 'recast' | 'edit';

/** The most versions one request asks for. */
export const MAX_VERSIONS = 3;

/**
 * The strategies of a request's versions, in their fixed order: none for `k = 1` (today's request). `k = 2` takes the first and
 * the last of the round's list, `k = 3` all of it. `retry` is true when every unit carries a previous version. With the
 * editorial guardrails (ED-191) the lists leave `recast` out, so `k = 2` and `k = 3` both ask for two versions.
 */
export function strategiesFor(k: number, retry: boolean, guardrails = false): StrategyId[] {
	const order = guardrails ? GUARDRAILS_STRATEGIES : VDATA.order;
	const all = retry ? order.retry : order.first;
	const n = Math.max(1, Math.min(MAX_VERSIONS, Math.floor(k)));
	if (n <= 1) return [];
	if (n === 2) return [all[0] as StrategyId, all[all.length - 1] as StrategyId];
	return [...all];
}

/** The position of a strategy in the fixed order (`edit` takes `minimal`'s place): the last tie-break of the ranking. */
export const strategyRank = (s: string): number =>
	s === 'minimal' || s === 'edit' ? 0 : s === 'restructure' ? 1 : s === 'recast' ? 2 : 3;

/* ---------------------------------------------------------------- markers */

export interface MarkerPair {
	open: string;
	close: string;
}

/** The marker characters (U+27E6, U+27E7). */
export const STANDARD_MARKERS: Readonly<MarkerPair> = Object.freeze({ ...DATA.markers });

/**
 * The marker pair for a request: the standard pair, or, when any of `texts` contains either standard character, the first
 * two private-use characters (U+E000…) that occur in none of them.
 */
export function markersFor(texts: readonly string[]): MarkerPair {
	const has = (c: string) => texts.some((t) => t.includes(c));
	if (!has(STANDARD_MARKERS.open) && !has(STANDARD_MARKERS.close)) return { ...STANDARD_MARKERS };
	const free: string[] = [];
	for (let cp = 0xe000; cp <= 0xf8ff && free.length < 2; cp++) {
		const c = String.fromCharCode(cp);
		if (!has(c)) free.push(c);
	}
	if (free.length < 2) throw new Error('no free private-use marker characters');
	return { open: free[0] as string, close: free[1] as string };
}

/** The marker id shown to the model for unit number `n`. */
export const markerId = (n: number): string => `U${n}`;

/* ---------------------------------------------------------------- the output contract */

/** The rewrite schema (§8.3): `additionalProperties: false` throughout. */
export const REWRITE_SCHEMA = {
	type: 'object',
	properties: {
		units: {
			type: 'array',
			items: {
				type: 'object',
				properties: { id: { type: 'string' }, text: { type: 'string' } },
				required: ['id', 'text'],
				additionalProperties: false,
			},
		},
	},
	required: ['units'],
	additionalProperties: false,
} as const;

/**
 * The schema of a request for `k > 1` versions: each unit carries a `versions` list of `{strategy, text}`, with `strategy`
 * restricted to the request's strategies. Fixed per strategy list, so it is the same for every request of a round class.
 */
export function versionsSchema(strategies: readonly StrategyId[]) {
	return versionsSchemaOf(strategies, VERSION_ITEM);
}

/** A version's fields without the guardrails: `{strategy, text}`. */
const VERSION_ITEM = { required: ['strategy', 'text'], extra: {} } as const;

/** The action of a passage or a version under the guardrails (GR2, plan §4.3, ED-194). */
export type UnitAction = 'rewrite' | 'keep';
const ACTION = { type: 'string', enum: ['rewrite', 'keep'] } as const;

/** A version's fields under the guardrails: `{strategy, action, why, text}` (the decision and its reason come before the text). */
const GR_VERSION_ITEM = {
	required: ['strategy', 'action', 'why', 'text'],
	extra: { action: ACTION, why: { type: 'string' } },
} as const;

/**
 * The rewrite schema under the guardrails (GR2, ED-194): each passage carries `action` and `why` as well. The schema asks for
 * both; the validator still reads a passage without `action` as a rewrite.
 */
export const GUARDRAILS_REWRITE_SCHEMA = {
	type: 'object',
	properties: {
		units: {
			type: 'array',
			items: {
				type: 'object',
				properties: {
					id: { type: 'string' },
					action: ACTION,
					why: { type: 'string' },
					text: { type: 'string' },
				},
				required: ['id', 'action', 'why', 'text'],
				additionalProperties: false,
			},
		},
	},
	required: ['units'],
	additionalProperties: false,
} as const;

/** The versions schema under the guardrails: each version carries `action` and `why` as well. */
export function guardrailsVersionsSchema(strategies: readonly StrategyId[]) {
	return versionsSchemaOf(strategies, GR_VERSION_ITEM);
}

function versionsSchemaOf(
	strategies: readonly StrategyId[],
	item: { required: readonly string[]; extra: Record<string, unknown> }
) {
	return {
		type: 'object',
		properties: {
			units: {
				type: 'array',
				items: {
					type: 'object',
					properties: {
						id: { type: 'string' },
						versions: {
							type: 'array',
							items: {
								type: 'object',
								properties: {
									strategy: { type: 'string', enum: [...strategies] },
									...item.extra,
									text: { type: 'string' },
								},
								required: [...item.required],
								additionalProperties: false,
							},
						},
					},
					required: ['id', 'versions'],
					additionalProperties: false,
				},
			},
		},
		required: ['units'],
		additionalProperties: false,
	} as const;
}

export type RewriteSchema =
	| typeof REWRITE_SCHEMA
	| ReturnType<typeof versionsSchema>
	| typeof GUARDRAILS_REWRITE_SCHEMA;

export const OUTPUT_CONTRACT: string = DATA.outputContract;
/** The output contract of a request for several versions (the system prompt; the strategy list rides in the user turn). */
export const VERSIONS_CONTRACT: string = VDATA.contract;

export interface UnitVersion {
	strategy: StrategyId;
	text: string;
	/** Under the guardrails (GR2): the version's action and its one-sentence reason. Absent otherwise. */
	action?: UnitAction;
	why?: string;
}

/** A passage's action and reason under the guardrails (GR2, ED-194). */
export interface UnitAnswer {
	action: UnitAction;
	why?: string;
}

export type UnitsValidation =
	| {
			ok: true;
			/** The text of each unit (with versions: its first version in strategy order). A kept unit's is its marked text. */
			texts: Map<string, string>;
			/** With `strategies`: every unit's versions, in the strategies' order whatever the order the model wrote them in. */
			versions?: Map<string, UnitVersion[]>;
			/** With `keep` and one version per unit: each unit's action and reason. */
			answers?: Map<string, UnitAnswer>;
	  }
	| { ok: false; reason: 'schema'; problems: string[] };

/**
 * The check of the guardrails' answer fields (GR2, plan §4.3): the text of each marked passage as the request marked it. A
 * `keep` must return that text (whitespace aside) with a reason, and every `why` is at most `WHY_MAX_CHARS` characters.
 */
export interface KeepCheck {
	originals: ReadonlyMap<string, string>;
}

const sameText = (a: string, b: string): boolean => {
	const n = (x: string) => x.normalize('NFC').replace(/\s+/g, ' ').trim();
	return n(a) === n(b);
};

/**
 * The action and reason of one passage or version under the guardrails, or null when they break the contract (the problems
 * are pushed). An absent `action` reads as a rewrite.
 */
function answerOf(
	entry: unknown,
	text: string,
	original: string | undefined,
	label: string,
	problems: string[]
): UnitAnswer | null {
	const a = (entry as { action?: unknown } | null)?.action;
	const why = (entry as { why?: unknown } | null)?.why;
	let ok = true;
	if (a !== undefined && a !== 'rewrite' && a !== 'keep') {
		problems.push(`${label} has an unexpected action ${String(a)}`);
		ok = false;
	}
	if (why !== undefined && typeof why !== 'string') {
		problems.push(`${label} has a "why" that is not a string`);
		ok = false;
	}
	const w = typeof why === 'string' ? why.trim() : '';
	if (w.length > WHY_MAX_CHARS) {
		problems.push(
			`${label} gives a reason of ${w.length} characters (one sentence, at most ${WHY_MAX_CHARS})`
		);
		ok = false;
	}
	if (a === 'keep') {
		if (!w) {
			problems.push(`${label} keeps the passage without a reason`);
			ok = false;
		}
		if (original === undefined || !sameText(text, original)) {
			problems.push(`${label} keeps the passage but its text differs from the marked passage`);
			ok = false;
		}
	}
	if (!ok) return null;
	return { action: a === 'keep' ? 'keep' : 'rewrite', ...(w ? { why: w } : {}) };
}

/**
 * Client-side validation of the answer (§8.3): the response is the parsed object (or its JSON text), and every marked id
 * appears exactly once with a string `text`, with no extra id. A failure has the reason `schema` (the caller retries once
 * with the same input).
 */
export function validateUnits(
	response: unknown,
	expected: readonly string[],
	strategies?: readonly StrategyId[],
	keep?: KeepCheck
): UnitsValidation {
	let r: unknown = response;
	if (typeof r === 'string') {
		try {
			r = JSON.parse(r);
		} catch {
			return { ok: false, reason: 'schema', problems: ['the response is not valid JSON'] };
		}
	}
	const units = (r as { units?: unknown } | null)?.units;
	if (!Array.isArray(units))
		return { ok: false, reason: 'schema', problems: ['the response has no "units" array'] };
	if (strategies !== undefined && strategies.length > 1)
		return validateVersions(units, expected, strategies, keep);
	// one text per unit: the check of v2, unchanged (with `keep`, the guardrails' fields are read as well)
	const problems: string[] = [];
	const texts = new Map<string, string>();
	const answers = new Map<string, UnitAnswer>();
	const want = new Set(expected);
	const dup = new Set<string>();
	/** Ids whose guardrails fields broke the contract (reported already; not also "missing"). */
	const bad = new Set<string>();
	for (const u of units) {
		const id = (u as { id?: unknown } | null)?.id;
		const text = (u as { text?: unknown } | null)?.text;
		if (typeof id !== 'string' || typeof text !== 'string') {
			problems.push('an entry lacks a string "id" or "text"');
			continue;
		}
		if (!want.has(id)) problems.push(`unexpected id ${id}`);
		else if (texts.has(id)) dup.add(id);
		else if (keep) {
			const original = keep.originals.get(id);
			const ans = answerOf(u, text, original, id, problems);
			if (!ans) {
				bad.add(id);
				continue;
			}
			answers.set(id, ans);
			texts.set(id, ans.action === 'keep' ? (original as string) : text);
		} else texts.set(id, text);
	}
	for (const id of dup) problems.push(`id ${id} appears more than once`);
	for (const id of expected)
		if (!texts.has(id) && !bad.has(id)) problems.push(`id ${id} is missing`);
	if (problems.length) return { ok: false, reason: 'schema', problems };
	return keep ? { ok: true, texts, answers } : { ok: true, texts };
}

/** The check of a request for several versions: every unit once, with exactly the request's strategies, each once. */
function validateVersions(
	units: readonly unknown[],
	expected: readonly string[],
	strategies: readonly StrategyId[],
	keep?: KeepCheck
): UnitsValidation {
	const problems: string[] = [];
	const texts = new Map<string, string>();
	const versions = new Map<string, UnitVersion[]>();
	const want = new Set(expected);
	const seen = new Set<string>();
	const dup = new Set<string>();
	for (const u of units) {
		const id = (u as { id?: unknown } | null)?.id;
		if (typeof id !== 'string') {
			problems.push('an entry lacks a string "id"');
			continue;
		}
		if (!want.has(id)) {
			problems.push(`unexpected id ${id}`);
			continue;
		}
		if (seen.has(id)) {
			dup.add(id);
			continue;
		}
		seen.add(id);
		const list = (u as { versions?: unknown } | null)?.versions;
		if (!Array.isArray(list)) {
			problems.push(`id ${id} has no "versions" list`);
			continue;
		}
		const byStrategy = new Map<string, string>();
		const answers = new Map<string, UnitAnswer>();
		let bad = false;
		for (const v of list) {
			const s = (v as { strategy?: unknown } | null)?.strategy;
			const t = (v as { text?: unknown } | null)?.text;
			if (typeof s !== 'string' || typeof t !== 'string') {
				problems.push(`a version of ${id} lacks a string "strategy" or "text"`);
				bad = true;
			} else if (!(strategies as readonly string[]).includes(s)) {
				problems.push(`${id} has an unexpected strategy ${s}`);
				bad = true;
			} else if (byStrategy.has(s)) {
				problems.push(`${id} has the strategy ${s} more than once`);
				bad = true;
			} else if (keep) {
				const original = keep.originals.get(id);
				const ans = answerOf(v, t, original, `the ${s} version of ${id}`, problems);
				if (!ans) {
					bad = true;
					byStrategy.set(s, t);
				} else {
					answers.set(s, ans);
					byStrategy.set(s, ans.action === 'keep' ? (original as string) : t);
				}
			} else byStrategy.set(s, t);
		}
		for (const s of strategies)
			if (!byStrategy.has(s)) {
				problems.push(`${id} lacks its ${s} version`);
				bad = true;
			}
		if (bad) continue;
		const ordered: UnitVersion[] = strategies.map((s) => {
			const a = answers.get(s);
			return {
				strategy: s,
				text: byStrategy.get(s) as string,
				...(a ? { action: a.action, ...(a.why ? { why: a.why } : {}) } : {}),
			};
		});
		versions.set(id, ordered);
		texts.set(id, (ordered[0] as UnitVersion).text);
	}
	for (const id of dup) problems.push(`id ${id} appears more than once`);
	for (const id of expected) if (!seen.has(id)) problems.push(`id ${id} is missing`);
	return problems.length
		? { ok: false, reason: 'schema', problems }
		: { ok: true, texts, versions };
}

/* ---------------------------------------------------------------- directives */

export interface DirectiveEvidenceV2 extends DirectiveEvidence {
	/** SR-01's repeated wording, when the run wants it in the system prompt (it then varies with the section). */
	sr01?: readonly string[];
}

export interface DirectiveSetInput {
	/** The targeted channels of the chunk. */
	channels: Iterable<ChannelId>;
	evidence?: DirectiveEvidenceV2;
	/** `repair` uses REPAIR_DIRECTIVES verbatim (translation scope), plus `TR01_REPAIR_DIRECTIVE` when a channel is TR-01 (ED-161). */
	mode?: 'deep' | 'repair';
	/** The editorial guardrails (ED-191): the conflicting directives take their guardrails texts. Repair mode is unchanged. */
	guardrails?: boolean;
}

/**
 * The directives of a channel set: the seed's, then the v3 additions (with the refrain and frame slots), then the v2
 * additions (SR-01, PT-02). The always-on rules are not included: `ALWAYS_ON` lists them.
 */
export function directivesV2(input: DirectiveSetInput): string[] {
	if (input.mode === 'repair') {
		const out = repairDirectives();
		// the TR-01 directive rides only in a request that targets TR-01: an MT-01-only request is REPAIR_DIRECTIVES verbatim
		for (const c of input.channels)
			if (c === 'TR-01') {
				out.push(TR01_REPAIR_DIRECTIVE);
				break;
			}
		return out;
	}
	const set = new Set<ChannelId>(input.channels);
	const alwaysCount = directiveData().seed.entries.filter((e) => e.when.length === 0).length;
	const all = buildDirectives(set, input.evidence);
	// with no vocabulary evidence the seed's text ends in ": ." — point to the notes instead
	const out = all
		.slice(0, all.length - alwaysCount)
		.map((d) => d.replace(/: \.$/, ' (the notes list them).'));
	for (const d of DATA.directives) {
		if (!d.when.some((c) => set.has(c as ChannelId))) continue;
		let text = d.text;
		const items = [...new Set(input.evidence?.sr01 ?? [])].slice(0, 5);
		if (d.suffix && items.length) text += d.suffix.replace('{items}', items.join('; '));
		out.push(text);
	}
	return input.guardrails ? withGuardrails(out) : out;
}

/**
 * The directive of a repair-mode request that targets TR-01 (DESIGN ED-161): the notes quote the machine-translation word
 * choices of each passage, and the passage's `Keep` block lists the words that must stay (ED-162). It never asks for a
 * particular word to be added.
 */
/** The heaviest machine-leaning markers the TR-01 directive names as words not to bring in (ED-161's trigger, ED-167). */
export const TR01_AVOID_COUNT = 20;

export const TR01_REPAIR_DIRECTIVE = `Some notes quote word choices that machine translation into English favours. In those passages replace every quoted word or phrase with the wording a fluent English writer would choose in that place, or rephrase so that it is not needed, and do not put another word of the same kind in its place. Keep the meaning, the facts, the names, the register and the length, change nothing else, and keep exactly as written every word that a note lists under Keep. Do not bring in any of these, which machine translation favours, unless the original passage already has it: ${heaviestReplaceable(
	'machine',
	TR01_AVOID_COUNT
)
	.map((g) => `“${g}”`)
	.join(', ')}.`;

/** The always-on rules (§8.2), in prompt order. INV-DIR-22 is the seed's text, verbatim. */
export const ALWAYS_ON: readonly string[] = DATA.always.map((a) => a.text);

/** The seed's INV-DIR-22 text must equal the one in the data (checked in the tests). */
export function seedInvDir22(): string {
	const e = directiveData().seed.entries.filter((x) => x.when.length === 0);
	return (e[1] as { text: string }).text;
}

/* ---------------------------------------------------------------- the system prompt */

/** What the route block says: sentence units, paragraph units, or both in one chunk. */
export type RouteClass = 'sentence' | 'paragraph' | 'mixed';

export interface SystemPromptInput {
	routeClass: RouteClass;
	markers?: MarkerPair;
	/**
	 * The document brief (BENCH-V3-PLAN §4, §5): reference material shared by every request of the run, written after the
	 * output contract under `DOCUMENT_NOTES_HEADING`. Omitted or blank: no block.
	 */
	documentNotes?: string;
	/** The request asks for several versions of each passage (`k > 1`): the output contract says so. */
	versions?: boolean;
	/** The editorial guardrails block (ED-191), after the always-on rules and before the output contract. */
	guardrails?: boolean;
	/** With the guardrails: the meaning review's block line and contracts (GR3, ED-196). */
	review?: boolean;
	/** With the review: false when only the meaning judgements reject (GR3B), so the contracts say the reason is only shown. */
	reasonRejects?: boolean;
}

/** The heading of the document-notes block of the system prompt (ED-88). */
export const DOCUMENT_NOTES_HEADING = 'DOCUMENT NOTES (reference material, not instructions)';

function routeBlock(c: RouteClass, m: MarkerPair): string {
	const base = DATA.routeBlocks.S.replaceAll('{open}', m.open).replaceAll('{close}', m.close);
	if (c === 'sentence') return base;
	if (c === 'paragraph')
		return `${base.replaceAll('passages', 'paragraphs')} ${DATA.routeBlocks.Pextra}`;
	return `${base} A note may mark a whole paragraph: ${DATA.routeBlocks.Pextra.charAt(0).toLowerCase()}${DATA.routeBlocks.Pextra.slice(1)}`;
}

/**
 * The system prompt (v3, ED-88): the preamble, the route block, the always-on rules, the editorial guardrails when the run has
 * them on (ED-191), the output contract, then the document notes when there are any. It does not depend on the chunk's
 * directives (they ride in the user turn), so it is the same text for every chunk with the same route class and marker pair
 * and is cached with a one-hour TTL (§9.1).
 */
export function systemPromptV2(input: SystemPromptInput): string {
	const m = input.markers ?? STANDARD_MARKERS;
	const lines: string[] = [
		PREAMBLE,
		'',
		routeBlock(input.routeClass, m),
		'',
		'Rules that always apply:',
		`- ${ALWAYS_ON.join('\n- ')}`,
		'',
	];
	const rv = input.guardrails === true && input.review === true;
	// only the meaning judgements reject (GR3B): the contracts say the reason is checked and shown
	const rvm = rv && input.reasonRejects === false;
	if (input.guardrails) lines.push(rv ? GUARDRAILS_REVIEW_SYSTEM : GUARDRAILS_SYSTEM, '');
	// under the guardrails the contract asks for each passage's action and reason (GR2, ED-194); with the review it says
	// that every rewrite and its reason are reviewed (GR3)
	if (input.guardrails)
		lines.push(
			input.versions
				? rv
					? rvm
						? GUARDRAILS_REVIEW_MEANING_VERSIONS_CONTRACT
						: GUARDRAILS_REVIEW_VERSIONS_CONTRACT
					: GUARDRAILS_VERSIONS_CONTRACT
				: rv
					? rvm
						? GUARDRAILS_REVIEW_MEANING_CONTRACT
						: GUARDRAILS_REVIEW_CONTRACT
					: GUARDRAILS_CONTRACT
		);
	else lines.push(input.versions ? VERSIONS_CONTRACT : OUTPUT_CONTRACT);
	const notes = input.documentNotes?.trim();
	if (notes) lines.push('', DOCUMENT_NOTES_HEADING, notes);
	return lines.join('\n');
}

/** The heading of the user turn's directive block (v3, ED-88). */
export const USER_DIRECTIVES_HEADING = 'Directives for this stretch (all mandatory):';

/** The neutral-paraphrase control instruction (§8.4). */
export const NEUTRAL_INSTRUCTION: string = DATA.neutral;

/** The judge's system text (§8.1). */
export const JUDGE_SYSTEM: string = DATA.routeBlocks.judge;

/* ---------------------------------------------------------------- per-channel notes */

/** The one-line pattern and the default instruction of each channel the bench can act on. */
export const CHANNEL_NOTES: Readonly<
	Partial<Record<ChannelId, { pattern: string; instruction: string }>>
> = {
	'LX-01': {
		pattern: 'a stock AI-flavoured word or intensifier',
		instruction: 'Replace it with plain, concrete wording.',
	},
	'LX-02': { pattern: 'a stock AI phrase', instruction: 'Say the same thing in plain wording.' },
	'LX-03': {
		pattern: 'an invented framework name or funnel-speak',
		instruction: 'Describe the idea plainly instead of naming it.',
	},
	// REFIT-S (ED-170): the paragraph is the unit; the directive proper is C-DIR-V2-LX04 in the user turn
	'LX-04': {
		pattern: 'varied wording: the same thing named by a different synonym each time',
		instruction:
			'Let the key content word recur where a careful writer would repeat it; do not cycle synonyms.',
	},
	'PT-01': {
		pattern: 'an em-dash or en-dash',
		instruction: 'Restructure the clause with commas, parentheses or a full stop.',
	},
	'ST-01': { pattern: 'a "not X but Y" contrast frame', instruction: 'Make the point directly.' },
	'ST-02': { pattern: 'a rule-of-three list', instruction: 'Use one, two or four items.' },
	'DS-01': {
		pattern: 'a sentence opened by a connective such as Moreover or However',
		instruction: 'Let the logic carry the transition.',
	},
	'DS-02': { pattern: 'a hedge', instruction: 'Cut it unless it is epistemically necessary.' },
	'DS-03': {
		pattern: 'an absolute pronouncement or a modal-passive necessity',
		instruction: 'State a grounded, particular claim instead.',
	},
	'SF-04': {
		pattern: 'an unsourced precise prescription',
		instruction: 'Soften it to an honest range or a qualified claim. Never invent a citation.',
	},
	'SF-05': {
		pattern: 'a uniformly glossed foreign or technical term',
		instruction: 'Keep a gloss only where a first-time reader needs one, and vary how it is given.',
	},
	'OB-01': {
		pattern: 'pipeline residue (markup, header hashes, code fences, citation brackets)',
		instruction: 'Remove the residue and keep the words.',
	},
	'PROC-03': {
		pattern: 'an abstract "the way X does Y" simile',
		instruction: 'Use a concrete image or a plain statement.',
	},
	'PROC-04': {
		pattern: 'a definition by negation: a "Not X." fragment or a "not X but Y" frame',
		instruction: 'Fold the fragment into the previous sentence and state what is, directly.',
	},
	'PROC-05': {
		pattern: 'an em-dash or italic emphasis span',
		instruction: 'Use plain punctuation; keep the stress only where it would otherwise be lost.',
	},
	'MT-01': {
		pattern: 'a machine-translation artefact',
		instruction: 'Restore natural, fluent English; keep the meaning exactly.',
	},
	// repair mode only (ED-161): the note's Evidence line quotes every strong marker of the passage
	'TR-01': {
		pattern: 'word choices that machine translation into English favours',
		instruction:
			'Replace each quoted word or phrase with the wording a fluent English writer would choose here, or rephrase so that it is not needed; keep the meaning, the facts, the names and the register, and change nothing else.',
	},
	'NA-01': {
		pattern:
			'engagement-essay choreography (hook question, fragment cascade, reveal pivot, direct address, closing question)',
		instruction: 'Say it plainly, without the staging.',
	},
	'ST-04': {
		pattern: 'consecutive sentences with the same opening frame',
		instruction: 'Restructure at least every other one.',
	},
	'ST-03': {
		pattern: 'repeated sentence openers',
		instruction: 'Vary how the sentences begin; avoid The/This/It/There and participial openings.',
	},
	'PROC-07': {
		pattern: 'a paragraph ending on a short epigram',
		instruction: 'End it on connective or concrete material.',
	},
	'PROC-06': {
		pattern: 'an unusually long or short sentence',
		instruction: 'Split a very long sentence; fold a fragment into its neighbour.',
	},
	'PROC-01': {
		pattern: 'a recurring phrase',
		instruction: 'Vary the wording here; the first occurrence stays as it is.',
	},
	'PROC-08': {
		pattern: 'a sentence frame reused with different fillers',
		instruction: 'Vary the syntax.',
	},
	'SR-01': {
		pattern: 'a four-word phrase repeated within the section',
		instruction: 'Vary the repeated wording.',
	},
	'RH-01': {
		pattern: 'uniform sentence length',
		instruction: 'Vary sentence length within this paragraph.',
	},
	'RH-02': {
		pattern: 'uniform sentence length',
		instruction: 'Vary sentence length within this paragraph.',
	},
	'PT-02': {
		pattern: 'uniform, low-variety punctuation',
		instruction: 'Use the punctuation the sense calls for.',
	},
};

/* ---------------------------------------------------------------- units and feedback */

/** Why an attempt failed, as the sentences the retry message carries (§6.7). */
export interface UnitFeedback {
	/** Failed meaning-check sentences. */
	checks?: readonly string[];
	/** Channel → quoted spans that still carry it. */
	residual?: ReadonlyArray<{ channel: ChannelId; spans: readonly string[] }>;
	/** Channel → quoted spans the rewrite introduced. */
	introduced?: ReadonlyArray<{ channel: ChannelId; spans: readonly string[] }>;
	/** The dialogue guard's explanations. */
	guard?: readonly string[];
	/** Under the guardrails (GR3): the prose review's sentences. */
	review?: readonly string[];
	/** R2 and R3 details (the window and the ledger). */
	window?: readonly string[];
	other?: readonly string[];
}

export interface RetryNote {
	/** Attempt number that produced `previous` (1-based). */
	attempt: number;
	previous: string;
	feedback: UnitFeedback;
}

export interface UnitSpec {
	/** The marker id (`U7`; see `markerId`). */
	id: string;
	/** Offsets of the unit in the chunk text. Units must not overlap. */
	start: number;
	end: number;
	/** `S` and `D` units are sentences unless `paragraph` is set; `P` units are paragraphs. */
	route: 'S' | 'P' | 'D';
	paragraph?: boolean;
	channels: readonly ChannelId[];
	/** Overrides the channels' default one-line patterns. */
	pattern?: string;
	/** Quoted evidence spans. */
	spans?: readonly string[];
	/** Replaces the default route instruction (for example "this phrase already appears 11 times in the document; vary it here"). */
	instruction?: string;
	/** A neutral-paraphrase control unit (§5.6, §8.4). */
	control?: boolean;
	/**
	 * The risk sheet (BENCH-V3-PLAN §5): checkable rules about what the measurer will penalise near this unit, tightest
	 * first, rendered as the `Keep` sub-block. Facts about the text, never advice.
	 */
	keep?: readonly string[];
	/** The meaning constraints (§5.2), one short sentence each, rendered as the `Meaning` sub-block. */
	meaning?: readonly string[];
	/**
	 * Listed motifs of the document brief that a targeted span of this unit is (ED-95): the unit is told the phrase is
	 * deliberate, not skipped.
	 */
	motifs?: ReadonlyArray<{ phrase: string; count: number }>;
	/**
	 * The unit is or overlaps a record (GR2, ED-193 (a)): a block quotation, a heading, an italic passage, a labelled entry or
	 * a quotation. Its note says so only under the guardrails; without them the field is not read.
	 */
	record?: UnitRecord;
	retry?: RetryNote;
}

const quoteSpans = (spans: readonly string[]): string => spans.map((s) => `“${s}”`).join(', ');

/** The feedback sentences of a failed attempt; the review's (GR3) only with the meaning review, so other paths ignore them. */
export function feedbackLinesV2(f: UnitFeedback, review = false): string[] {
	const out: string[] = [];
	for (const s of f.residual ?? [])
		out.push(`These passages still carry ${s.channel}: ${quoteSpans(s.spans)}`);
	for (const s of f.introduced ?? [])
		out.push(`These changes introduced ${s.channel}: ${quoteSpans(s.spans)}`);
	for (const m of f.checks ?? []) out.push(`Meaning check failed: ${m}`);
	for (const g of f.guard ?? []) out.push(`Dialogue check failed: ${g}`);
	if (review) for (const r of f.review ?? []) out.push(`Review failed: ${r}`);
	for (const w of f.window ?? []) out.push(w);
	for (const o of f.other ?? []) out.push(o);
	return out;
}

export function routeClassOf(units: readonly UnitSpec[]): RouteClass {
	const para = units.filter((u) => u.route === 'P' || u.paragraph).length;
	if (para === 0) return 'sentence';
	return para === units.length ? 'paragraph' : 'mixed';
}

/** The channel set whose directives a chunk's request carries (control units contribute none). */
export function targetedChannels(units: readonly UnitSpec[]): ChannelId[] {
	const s = new Set<ChannelId>();
	for (const u of units) if (!u.control) for (const c of u.channels) s.add(c);
	return [...s];
}

function noteOf(u: UnitSpec, guardrails = false, review = false): string[] {
	const lines = [`${u.id}${u.control ? ' · control' : ` · ${u.channels.join(', ')}`}`];
	if (u.control) {
		lines.push(`  Do: ${u.instruction ?? NEUTRAL_INSTRUCTION}`);
	} else {
		const pattern =
			u.pattern ??
			[
				...new Set(
					u.channels.map((c) => CHANNEL_NOTES[c]?.pattern).filter((x): x is string => !!x)
				),
			].join('; ');
		if (pattern) lines.push(`  Pattern: ${pattern}`);
		if (u.spans && u.spans.length > 0) lines.push(`  Evidence: ${quoteSpans(u.spans)}`);
		const instruction =
			u.instruction ??
			[
				...new Set(
					u.channels
						.map((c) => noteInstruction(c, guardrails, CHANNEL_NOTES[c]?.instruction))
						.filter((x): x is string => !!x)
				),
			].join(' ');
		if (instruction) lines.push(`  Do: ${instruction}`);
		if (guardrails && u.record && u.record.kinds.length > 0) lines.push(recordLine(u.record));
	}
	// the risk sheet and the brief's motifs (BENCH-V3-PLAN §5, §8): not for the neutral-paraphrase controls
	for (const m of u.motifs ?? [])
		lines.push(
			`  Motif: “${m.phrase}” is a deliberate motif of this document (${m.count} times); keep it word for word and address the other channels.`
		);
	if (u.keep && u.keep.length > 0) lines.push('  Keep:', ...u.keep.map((l) => `   - ${l}`));
	if (u.meaning && u.meaning.length > 0)
		lines.push('  Meaning:', ...u.meaning.map((l) => `   - ${l}`));
	if (u.retry) {
		lines.push(`  Your version (attempt ${u.retry.attempt}):`);
		for (const l of u.retry.previous.split('\n')) lines.push(`  > ${l}`);
		const fb = feedbackLinesV2(u.retry.feedback, review);
		if (fb.length > 0) lines.push('  Feedback:', ...fb.map((l) => `   - ${l}`));
	}
	return lines;
}

/** The chunk text with every unit wrapped in its markers. */
export function markChunk(
	text: string,
	units: readonly UnitSpec[],
	m: MarkerPair = STANDARD_MARKERS
): string {
	const sorted = [...units].sort((a, b) => a.start - b.start);
	let out = '';
	let pos = 0;
	for (const u of sorted) {
		if (u.start < pos || u.end < u.start || u.end > text.length)
			throw new Error(`unit ${u.id} overlaps another unit or leaves the chunk`);
		out += text.slice(pos, u.start);
		out += `${m.open}${u.id}${m.close}${text.slice(u.start, u.end)}${m.open}/${u.id}${m.close}`;
		pos = u.end;
	}
	return out + text.slice(pos);
}

/* ---------------------------------------------------------------- the request */

export interface ChunkRequestInput {
	/** Paragraphs [a, b) of the chunk and its boundary version. */
	a: number;
	b: number;
	v: number;
	/** `part i of n` when the chunk is a piece of an oversize paragraph. */
	piece?: { index: number; count: number };
	/** The chunk's current text (`chunkText`). */
	text: string;
	units: readonly UnitSpec[];
	evidence?: DirectiveEvidenceV2;
	mode?: 'deep' | 'repair';
	/** The document brief for the system prompt (`SystemPromptInput.documentNotes`); nothing passes it before session S-B. */
	documentNotes?: string;
	/**
	 * Versions per unit (BENCH-V3-PLAN §6): 1 (default) is today's request; 2 or 3 asks for that many named versions of every
	 * passage, in one call.
	 */
	versions?: number;
	/**
	 * The editorial guardrails (REWRITE-GUARDRAILS-PLAN §4.2, ED-191): the system block, the directive and note variants, and
	 * the strategies without `recast`. Absent or false: the baseline's request, byte for byte (T-GR-OFF-01).
	 */
	guardrails?: boolean;
	/**
	 * With the guardrails, the meaning review (GR3, ED-196; dormant by default, ED-197): its system line, contracts, directives
	 * heading, retry prefaces and the review's feedback lines. Absent: GR2's request.
	 */
	review?: boolean;
	/**
	 * With the review: false when only the meaning judgements reject (GR3B, the operator's variant of 2026-10-21); only the
	 * contracts change. Absent: GR3's request.
	 */
	reasonRejects?: boolean;
}

export interface ChunkRequest {
	system: string;
	user: string;
	schema: RewriteSchema;
	/** The marker ids the answer must contain, in text order. */
	unitIds: string[];
	markers: MarkerPair;
	/** True when the chunk carries units that have a previous attempt (a retry round). */
	retry: boolean;
	/** The strategies of the versions the answer must carry per unit, in order. Absent for a request of one version. */
	strategies?: StrategyId[];
	/**
	 * Under the guardrails (GR2, ED-194): the answer may keep a passage (`action: "keep"`, the marked text, a reason). Absent
	 * without them, so the request is the baseline's.
	 */
	keepable?: true;
}

const RETRY_PREFACE =
	'This is a retry. For each marked passage, EDIT YOUR PREVIOUS VERSION (shown in the notes) so that it addresses the feedback; do not start again from the original. Passages that are no longer marked were accepted and are shown with their new text.';

/** The complete request for one chunk: the system prompt, the user message, the schema and the expected ids. */
export function buildChunkRequest(input: ChunkRequestInput): ChunkRequest {
	if (input.units.length === 0) throw new Error('a chunk request needs at least one unit');
	const units = [...input.units].sort((a, b) => a.start - b.start);
	const texts = [
		input.text,
		...units.flatMap((u) => [u.retry?.previous ?? '', ...(u.spans ?? [])]),
	];
	const markers = markersFor(texts);
	const allControl = units.every((u) => u.control);
	const retry = units.some((u) => u.retry);
	const gr = input.guardrails === true;
	const rv = gr && input.review === true;
	const strategies = strategiesFor(
		input.versions ?? 1,
		units.every((u) => u.retry),
		gr
	);
	const multi = strategies.length > 1;
	const part = input.piece ? ` (part ${input.piece.index + 1} of ${input.piece.count})` : '';
	const para = input.b - input.a > 1 ? `¶${input.a + 1}–¶${input.b}` : `¶${input.a + 1}`;
	const lines: string[] = [`CHUNK ${para}${part} (version ${input.v})`, ''];
	// with the meaning review (GR3) a retry may keep the passage; repair mode keeps the baseline's preface
	const grRetry = rv && input.mode !== 'repair';
	if (retry)
		lines.push(
			multi
				? grRetry
					? GUARDRAILS_VERSIONS_RETRY_PREFACE
					: VDATA.retryPreface
				: grRetry
					? GUARDRAILS_RETRY_PREFACE
					: RETRY_PREFACE,
			''
		);
	// the chunk's directives (ED-88): a control-only request carries none
	const directives = allControl
		? []
		: directivesV2({
				channels: targetedChannels(units),
				evidence: input.evidence,
				mode: input.mode,
				...(gr ? { guardrails: true } : {}),
			});
	if (directives.length > 0)
		lines.push(
			rv && input.mode !== 'repair' ? GUARDRAILS_DIRECTIVES_HEADING : USER_DIRECTIVES_HEADING,
			`- ${directives.join('\n- ')}`,
			''
		);
	if (multi)
		lines.push(
			VDATA.heading,
			...strategies.map((s) =>
				VDATA.line.replace('{strategy}', s).replace('{text}', VDATA.strategies[s])
			),
			''
		);
	lines.push(markChunk(input.text, units, markers), '', 'NOTES');
	for (const u of units) lines.push(...noteOf(u, gr && input.mode !== 'repair', rv));
	return {
		system: systemPromptV2({
			routeClass: routeClassOf(units),
			markers,
			...(input.documentNotes !== undefined ? { documentNotes: input.documentNotes } : {}),
			...(multi ? { versions: true } : {}),
			...(gr ? { guardrails: true } : {}),
			...(rv ? { review: true } : {}),
			...(rv && input.reasonRejects === false ? { reasonRejects: false } : {}),
		}),
		user: lines.join('\n'),
		schema: multi
			? gr
				? guardrailsVersionsSchema(strategies)
				: versionsSchema(strategies)
			: gr
				? GUARDRAILS_REWRITE_SCHEMA
				: REWRITE_SCHEMA,
		unitIds: units.map((u) => u.id),
		markers,
		retry,
		...(multi ? { strategies } : {}),
		...(gr ? { keepable: true as const } : {}),
	};
}

/**
 * The text of each marked passage as the request marked it (between the open and close markers of its id in the user turn):
 * what a `keep` answer must return.
 */
export function markedTexts(
	req: Pick<ChunkRequest, 'user' | 'unitIds' | 'markers'>
): Map<string, string> {
	const out = new Map<string, string>();
	const { open, close } = req.markers;
	for (const id of req.unitIds) {
		const a = req.user.indexOf(`${open}${id}${close}`);
		if (a < 0) continue;
		const from = a + open.length + id.length + close.length;
		const b = req.user.indexOf(`${open}/${id}${close}`, from);
		if (b >= 0) out.set(id, req.user.slice(from, b));
	}
	return out;
}

/** `validateUnits` with the request's own expectations: its ids, its strategies and, under the guardrails, the keep check. */
export function validateAnswer(parsed: unknown, req: ChunkRequest): UnitsValidation {
	return validateUnits(
		parsed,
		req.unitIds,
		req.strategies,
		req.keepable ? { originals: markedTexts(req) } : undefined
	);
}
